import { Form, Card, Badge, Button, Alert, Spinner } from 'react-bootstrap'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-toastify'

type AiProvider = 'DIFY' | 'LLM'
type LlmProvider = 'ANTHROPIC' | 'OPENAI' | ''

interface DifySectionProps {
  difyEnabled: boolean
  difyApiKey: string
  difyApiUrl: string
  onDifyEnabledChange: (value: boolean) => void
  onDifyApiKeyChange: (value: string) => void
  onDifyApiUrlChange: (value: string) => void
  difyAppId: string
  onDifyAppIdChange: (value: string) => void
  organizationId: string
  switchLabel?: string
  // IA Conversacional: selector de proveedor (Dify vs LLM Directo)
  aiProvider?: AiProvider
  onAiProviderChange?: (value: AiProvider) => void
  llmProvider?: LlmProvider
  onLlmProviderChange?: (value: LlmProvider) => void
  llmApiKey?: string
  onLlmApiKeyChange?: (value: string) => void
  llmModel?: string
  onLlmModelChange?: (value: string) => void
}

const LLM_MODEL_PLACEHOLDER: Record<Exclude<LlmProvider, ''>, string> = {
  ANTHROPIC: 'claude-sonnet-5',
  OPENAI: 'gpt-5',
}

export const DifySection = ({
  difyEnabled,
  difyApiKey,
  difyApiUrl,
  onDifyEnabledChange,
  onDifyApiKeyChange,
  onDifyApiUrlChange,
  difyAppId,
  onDifyAppIdChange,
  organizationId,
  switchLabel,
  aiProvider = 'DIFY',
  onAiProviderChange,
  llmProvider = 'ANTHROPIC',
  onLlmProviderChange,
  llmApiKey = '',
  onLlmApiKeyChange,
  llmModel = '',
  onLlmModelChange,
}: DifySectionProps) => {
  const [testingConnection, setTestingConnection] = useState(false)
  const [connectionStatus, setConnectionStatus] = useState<'success' | 'error' | null>(null)
  const [connectionMessage, setConnectionMessage] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)

  const handleTestConnection = async () => {
    if (!difyApiKey || !difyApiUrl) {
      toast.warning('Por favor completa API Key y URL')
      return
    }

    setTestingConnection(true)
    try {
      // Intentar obtener información de la API de Dify usando el endpoint de parámetros
      const response = await axios.get(`${difyApiUrl}/parameters`, {
        headers: { Authorization: `Bearer ${difyApiKey}` },
        timeout: 5000,
      })

      // Si llegamos aquí, la conexión fue exitosa
      setConnectionStatus('success')
      setConnectionMessage(`✅ Conexión verificada exitosamente`)
      toast.success('Conexión a Dify exitosa')
    } catch (error: any) {
      setConnectionStatus('error')
      const errorMsg = error.response?.status === 401
        ? 'API Key inválida'
        : error.message || 'Error de conexión'
      setConnectionMessage(`❌ Error: ${errorMsg}`)
      toast.error(`No se pudo conectar a Dify: ${errorMsg}`)
    } finally {
      setTestingConnection(false)
    }
  }

  // Limpiar estado cuando se deshabilita
  useEffect(() => {
    if (!difyEnabled) {
      setConnectionStatus(null)
      setConnectionMessage('')
    }
  }, [difyEnabled])

  const providerBadgeLabel = !difyEnabled
    ? 'Deshabilitado'
    : aiProvider === 'LLM'
    ? `LLM Directo${llmProvider ? ` (${llmProvider === 'ANTHROPIC' ? 'Claude' : 'GPT'})` : ''}`
    : 'Dify'

  return (
    <Card className="mb-4 border-0 shadow-sm">
      <Card.Header className="bg-light border-bottom">
        <div className="d-flex align-items-center justify-content-between">
          <h5 className="mb-0">
            <span className="me-2">🤖</span>
            IA Conversacional
          </h5>
          <Badge bg={difyEnabled ? 'success' : 'secondary'}>
            {providerBadgeLabel}
          </Badge>
        </div>
      </Card.Header>
      <Card.Body>
        <Form.Group className="mb-3">
          <Form.Check
            type="switch"
            label={switchLabel || "Habilitar IA Conversacional para este nivel"}
            checked={difyEnabled}
            onChange={(e) => onDifyEnabledChange(e.target.checked)}
            id="dify-enabled-switch"
          />
          <Form.Text className="text-muted d-block mt-2">
            Cuando está habilitado, los agentes pueden usar IA conversacional (Dify o un
            LLM directo) para generar respuestas automáticas.
          </Form.Text>
        </Form.Group>

        {difyEnabled && (
          <>
            {/* Selector de proveedor */}
            <Form.Group className="mb-3">
              <Form.Label>Proveedor de IA</Form.Label>
              <Form.Select
                value={aiProvider}
                onChange={(e) => onAiProviderChange?.(e.target.value as AiProvider)}
              >
                <option value="DIFY">Dify</option>
                <option value="LLM">LLM Directo</option>
              </Form.Select>
              <Form.Text className="text-muted d-block mt-2">
                Elegí si este nivel usa Dify (motor + base de conocimiento externa) o un
                LLM directo (Anthropic/OpenAI) con tus propias credenciales.
              </Form.Text>
            </Form.Group>

            {aiProvider === 'DIFY' && (
              <>
                {/* API Key */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    API Key
                    <span className="text-danger ms-1">*</span>
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? 'text' : 'password'}
                      value={difyApiKey}
                      onChange={(e) => onDifyApiKeyChange(e.target.value)}
                      placeholder="app-xxxxxxxxxxxxx"
                      className="form-control"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowApiKey(!showApiKey)}
                      title={showApiKey ? 'Ocultar' : 'Mostrar'}
                    >
                      {showApiKey ? '👁️‍🗨️' : '🔒'}
                    </Button>
                  </div>
                  <Form.Text className="text-muted d-block mt-2">
                    Obtén tu API Key en tu{' '}
                    <a href="https://cloud.dify.ai" target="_blank" rel="noopener noreferrer">
                      dashboard de Dify
                    </a>
                  </Form.Text>
                </Form.Group>

                {/* API URL */}
                <Form.Group className="mb-3">
                  <Form.Label>API URL</Form.Label>
                  <Form.Control
                    type="url"
                    value={difyApiUrl}
                    onChange={(e) => onDifyApiUrlChange(e.target.value)}
                    placeholder="https://api.dify.ai/v1"
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    Por defecto: https://api.dify.ai/v1
                  </Form.Text>
                </Form.Group>

                {/* App ID */}
                <Form.Group className="mb-3">
                  <Form.Label>App ID</Form.Label>
                  <Form.Control
                    type="text"
                    value={difyAppId}
                    onChange={(e) => onDifyAppIdChange(e.target.value)}
                    placeholder="UUID de la aplicación en Dify"
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    Requerido para enrutar los mensajes a la App correcta.
                  </Form.Text>
                </Form.Group>

                {/* Botón Test Connection */}
                <div className="mb-3">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !difyApiKey}
                    className="me-2"
                  >
                    {testingConnection ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Probando...
                      </>
                    ) : (
                      '💾 Probar conexión'
                    )}
                  </Button>
                  <Form.Text className="text-muted">
                    Verifica que tu API Key sea válida
                  </Form.Text>
                </div>

                {/* Connection Status Alert */}
                {connectionStatus && (
                  <Alert
                    variant={connectionStatus === 'success' ? 'success' : 'danger'}
                    className="mb-3"
                    dismissible
                    onClose={() => setConnectionStatus(null)}
                  >
                    <small>{connectionMessage}</small>
                  </Alert>
                )}
              </>
            )}

            {aiProvider === 'LLM' && (
              <>
                {/* Proveedor de LLM */}
                <Form.Group className="mb-3">
                  <Form.Label>Proveedor de LLM</Form.Label>
                  <Form.Select
                    value={llmProvider}
                    onChange={(e) => onLlmProviderChange?.(e.target.value as LlmProvider)}
                  >
                    <option value="ANTHROPIC">Anthropic (Claude)</option>
                    <option value="OPENAI">OpenAI (GPT)</option>
                  </Form.Select>
                </Form.Group>

                {/* API Key del LLM */}
                <Form.Group className="mb-3">
                  <Form.Label>
                    API Key
                    <span className="text-danger ms-1">*</span>
                  </Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type={showApiKey ? 'text' : 'password'}
                      value={llmApiKey}
                      onChange={(e) => onLlmApiKeyChange?.(e.target.value)}
                      placeholder={llmProvider === 'OPENAI' ? 'sk-...' : 'sk-ant-...'}
                      className="form-control"
                    />
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowApiKey(!showApiKey)}
                      title={showApiKey ? 'Ocultar' : 'Mostrar'}
                    >
                      {showApiKey ? '👁️‍🗨️' : '🔒'}
                    </Button>
                  </div>
                </Form.Group>

                {/* Modelo */}
                <Form.Group className="mb-3">
                  <Form.Label>Modelo</Form.Label>
                  <Form.Control
                    type="text"
                    value={llmModel}
                    onChange={(e) => onLlmModelChange?.(e.target.value)}
                    placeholder={llmProvider ? LLM_MODEL_PLACEHOLDER[llmProvider] : ''}
                  />
                  <Form.Text className="text-muted d-block mt-2">
                    Ej: {llmProvider ? LLM_MODEL_PLACEHOLDER[llmProvider] : 'claude-sonnet-5 / gpt-5'}
                  </Form.Text>
                </Form.Group>
              </>
            )}

            {/* Info Box */}
            <div className="bg-light p-3 rounded border-start border-info border-4">
              <small className="text-muted d-block">
                <strong>💡 Nota:</strong> Los agentes pueden usar su propia configuración
                de IA Conversacional en su perfil, lo que overridea esta configuración.
              </small>
            </div>
          </>
        )}

        {!difyEnabled && (
          <Alert variant="info" className="mb-0">
            <small>
              ℹ️ IA Conversacional está deshabilitada. Habilita esta opción para usar
              conversaciones inteligentes powered by IA (Dify o LLM Directo).
            </small>
          </Alert>
        )}
      </Card.Body>
    </Card>
  )
}
